import { AdvisorPortalNetwork, ContentService, HasSubscriptionsToClose } from '@agingplan';
import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { CommonService } from 'src/app/services/common.service';
import { NavigationService } from 'src/app/services/navigation.service';

/**
 * Component responsible for managing LTC (Long Term Care) conversation content and navigation.
 * Handles article display, footer visibility, and network disclosure management.
 */
@Component({
  selector: 'app-ltc-conversation',
  templateUrl: './ltc-conversation.component.html',
  styleUrls: ['./ltc-conversation.component.scss'],
})
export class LtcConversationComponent extends HasSubscriptionsToClose() implements OnDestroy {
  /**
   * Initializes the component and sets up route monitoring
   * @param route - Angular's route service for accessing URL parameters
   * @param router - Angular's router service for navigation events
   * @param contentService - Service managing content and articles
   * @param navigationService - Service handling navigation-related functionality
   * @param commonService - Service for common functionality across components
   */
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private contentService: ContentService,
    private navigationService: NavigationService,
    private commonService: CommonService,
  ) {
    super();

    // Monitor URL changes and check active routes
    this.subscribeTo(
      this.route.url.subscribe(() => {
        this.checkIfAnyRouteActive();
      }),
    );

    // Monitor navigation events to update disclosure and check active routes
    this.subscribeTo(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.contentService.disclosure = this.route.snapshot.paramMap.get('disclosure');
          this.checkIfAnyRouteActive();
        }
      }),
    );
  }

  /**
   * Updates the current article based on the route parameters.
   * Manages footer visibility based on article configuration.
   * Displays error if article cannot be found.
   */
  public checkIfAnyRouteActive() {
    // Get articles and process the current article
    this.subscribeTo(
      this.contentService.articles$.subscribe((articles) => {
        if (articles.length > 0) {
          const article_id = parseInt(this.route.snapshot.paramMap.get('article_id') || '0');
          const article = this.contentService.getArticle(article_id);

          // Manage footer visibility based on article configuration
          if (article?.heading?.footer) {
            this.navigationService.showFooter();
          } else {
            this.navigationService.hideFooter();
          }

          if (!article) {
            return alert('No article with that Id can be found!');
          }

          this.contentService.article$.next(article);
        }
      }),
    );
  }

  /**
   * Updates the common service with network disclosure information
   * @param network - The advisor portal network containing disclosure information
   */
  public getNetworkDisclosure(network: AdvisorPortalNetwork) {
    this.commonService.footer_disclosure = {
      disclosure: network.disclosure,
      email: '',
      phone: '',
    };
  }

  /**
   * Cleanup on component destruction.
   * Clears footer disclosure and handles subscription cleanup via HasSubscriptionsToClose.
   */
  ngOnDestroy(): void {
    this.commonService.footer_disclosure = null;
    // Parent class (HasSubscriptionsToClose) handles subscription cleanup
  }
}
